import { Link } from 'react-router-dom'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog'
import { Badge } from '@/components/ui/Badge'
import type { CaseDetail } from '../types'
import type { AlertSummary } from '@/features/alerts/types'

/**
 * Drill-down dialog for an Investigation Actions row. Surfaces the
 * underlying data the row was derived from — BVN flags / profile
 * change events / velocity transactions / counterparty cluster /
 * disposition history — so the analyst can confirm or override the
 * automated check inline without leaving the case workspace.
 */
export function InvestigationActionDialog({
  open,
  onOpenChange,
  actionKey,
  title,
  description,
  c,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  actionKey: string
  title: string
  description: string
  c: CaseDetail
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="max-h-[28rem] overflow-y-auto pr-1">
          {actionKey === 'bvn_kyc' ? <BvnKycDetail c={c} /> : null}
          {actionKey === 'profile_change' ? <ProfileChangeDetail c={c} /> : null}
          {actionKey === 'velocity' ? <VelocityDetail c={c} /> : null}
          {actionKey === 'counterparty' ? <CounterpartyDetail c={c} /> : null}
          {actionKey === 'disposition' ? <DispositionDetail c={c} /> : null}
        </div>
      </DialogContent>
    </Dialog>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Per-action panels
// ─────────────────────────────────────────────────────────────────────────────

function BvnKycDetail({ c }: { c: CaseDetail }) {
  const cust = c.primary_customer
  const flagged = c.linked_alerts.filter((a) => {
    const flags = (a as unknown as { flags?: string[] }).flags ?? []
    return flags.some((f) => /bvn|kyc/i.test(f))
  })
  return (
    <div className="space-y-3 text-sm">
      <Section label="Subject customer">
        {cust ? (
          <Link
            to={`/customers/${cust.id}`}
            className="font-medium text-primary hover:underline"
          >
            {cust.display_name} <span className="font-mono text-xs text-muted-fg">· {cust.customer_number}</span>
          </Link>
        ) : (
          <p className="text-muted-fg">No primary customer linked.</p>
        )}
      </Section>
      <Section label={`BVN / KYC flags on linked alerts (${flagged.length})`}>
        {flagged.length === 0 ? (
          <p className="text-xs text-muted-fg">
            No alert on this case carries a BVN or KYC flag. Confirm the customer&apos;s BVN
            against the current institutional + CBN watchlists before checking off.
          </p>
        ) : (
          <ul className="space-y-1.5">
            {flagged.map((a) => (
              <AlertRow key={a.id} a={a} />
            ))}
          </ul>
        )}
      </Section>
      <Section label="Suggested next step">
        <p className="text-xs text-muted-fg">
          Open the customer 360 → Compliance tab to review their BVN match status, KYC
          tier ceiling, and any outstanding documentation requests.
        </p>
      </Section>
    </div>
  )
}

function ProfileChangeDetail({ c }: { c: CaseDetail }) {
  const events = (c.timeline ?? []).filter((t) =>
    /profile|reset|pin|kyc|email|phone/i.test(t.message),
  )
  return (
    <div className="space-y-3 text-sm">
      <Section label={`Profile / PIN / contact changes on the case timeline (${events.length})`}>
        {events.length === 0 ? (
          <p className="text-xs text-muted-fg">
            No profile-change events surfaced in the case timeline. Cross-check against
            the customer 360 timeline (full audit trail) before checking off.
          </p>
        ) : (
          <ol className="space-y-2 border-l border-border pl-3">
            {events.map((e) => (
              <li key={e.id} className="text-xs">
                <p className="font-mono text-[0.65rem] uppercase text-muted-fg">
                  {new Date(e.at).toLocaleString()}
                </p>
                <p className="mt-0.5">{e.message}</p>
                {e.by ? (
                  <p className="mt-0.5 text-[0.65rem] text-muted-fg">
                    by {e.by.display_name}
                  </p>
                ) : null}
              </li>
            ))}
          </ol>
        )}
      </Section>
    </div>
  )
}

function VelocityDetail({ c }: { c: CaseDetail }) {
  const alerts = c.linked_alerts
  const total = alerts.reduce((acc, a) => acc + (a.amount_minor ?? 0), 0)
  return (
    <div className="space-y-3 text-sm">
      <Section
        label={`Linked transactions (${alerts.length}) · cumulative ₦${
          (total / 100).toLocaleString('en-NG', { maximumFractionDigits: 2 })
        }`}
      >
        {alerts.length === 0 ? (
          <p className="text-xs text-muted-fg">No alerts linked.</p>
        ) : (
          <table className="w-full text-xs">
            <thead className="text-[0.65rem] uppercase text-muted-fg">
              <tr>
                <th scope="col" className="text-left">Alert</th>
                <th scope="col" className="text-left">Channel</th>
                <th scope="col" className="text-right">Amount (₦)</th>
                <th scope="col" className="text-left">When</th>
              </tr>
            </thead>
            <tbody>
              {alerts.map((a) => (
                <tr key={a.id} className="border-t border-border">
                  <td className="py-1">
                    <Link
                      to={`/alerts/${a.id}`}
                      className="font-mono text-primary hover:underline"
                    >
                      {a.alert_number}
                    </Link>
                  </td>
                  <td className="py-1 capitalize">
                    {String(a.channel ?? '—').replace('_', ' ')}
                  </td>
                  <td className="py-1 text-right font-mono tabular-nums">
                    {a.amount_minor != null
                      ? (a.amount_minor / 100).toLocaleString('en-NG', {
                          maximumFractionDigits: 2,
                        })
                      : '—'}
                  </td>
                  <td className="py-1 text-muted-fg">
                    {new Date(a.created_at).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>
      <Section label="Velocity threshold">
        <p className="text-xs text-muted-fg">
          A case is flagged HIGH velocity when ≥3 linked alerts OR cumulative amount
          ≥₦100,000. Adjust the thresholds in scenario settings if this is producing
          false positives.
        </p>
      </Section>
    </div>
  )
}

function CounterpartyDetail({ c }: { c: CaseDetail }) {
  const counts = new Map<string, { count: number; alerts: AlertSummary[] }>()
  for (const a of c.linked_alerts) {
    const ben = (a as unknown as { transaction?: { beneficiary_nuban?: string } })
      .transaction?.beneficiary_nuban
    if (!ben) continue
    const e = counts.get(ben) ?? { count: 0, alerts: [] }
    e.count++
    e.alerts.push(a)
    counts.set(ben, e)
  }
  const groups = Array.from(counts.entries())
    .sort((a, b) => b[1].count - a[1].count)
  return (
    <div className="space-y-3 text-sm">
      <Section label={`Beneficiary clusters across linked alerts (${groups.length} unique)`}>
        {groups.length === 0 ? (
          <p className="text-xs text-muted-fg">
            No beneficiary NUBAN data on the linked alerts&apos; transactions. Open
            the relevant alert detail page to inspect the transaction record manually.
          </p>
        ) : (
          <ul className="space-y-2">
            {groups.map(([nuban, group]) => (
              <li
                key={nuban}
                className="rounded border border-border bg-muted/30 p-2 text-xs"
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono">{nuban}</span>
                  <Badge variant={group.count >= 3 ? 'critical' : group.count >= 2 ? 'pending' : 'default'}>
                    {group.count} hit{group.count === 1 ? '' : 's'}
                  </Badge>
                </div>
                <ul className="mt-1.5 flex flex-wrap gap-1">
                  {group.alerts.map((a) => (
                    <Link
                      key={a.id}
                      to={`/alerts/${a.id}`}
                      className="font-mono text-[0.65rem] text-primary hover:underline"
                    >
                      {a.alert_number}
                    </Link>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        )}
      </Section>
      <Section label="Cluster threshold">
        <p className="text-xs text-muted-fg">
          A counterparty is flagged when the same NUBAN appears on ≥2 alerts in this
          case. Repeated hits to the same beneficiary suggest a mule chain or romance-
          scam pattern.
        </p>
      </Section>
    </div>
  )
}

function DispositionDetail({ c }: { c: CaseDetail }) {
  const lead = c.lead_analyst
  const last = (c.timeline ?? [])[(c.timeline ?? []).length - 1]
  const isTerminal =
    c.status === 'resolved' ||
    c.status === 'closed' ||
    c.status === 'fraud_confirmed' ||
    c.status === 'false_positive'
  return (
    <div className="space-y-3 text-sm">
      <Section label="Current state">
        <div className="flex items-center gap-2">
          <Badge variant={isTerminal ? 'success' : 'pending'}>
            {c.status.replace('_', ' ')}
          </Badge>
          {lead ? (
            <span className="text-xs text-muted-fg">
              · led by <span className="font-medium text-fg">{lead.display_name}</span>
            </span>
          ) : null}
        </div>
      </Section>
      {c.summary ? (
        <Section label="Investigation summary">
          <p className="text-xs leading-relaxed">{c.summary}</p>
        </Section>
      ) : null}
      {last ? (
        <Section label="Most recent timeline entry">
          <p className="font-mono text-[0.65rem] uppercase text-muted-fg">
            {new Date(last.at).toLocaleString()}
          </p>
          <p className="mt-0.5 text-xs">{last.message}</p>
          {last.by ? (
            <p className="mt-0.5 text-[0.65rem] text-muted-fg">
              by {last.by.display_name}
            </p>
          ) : null}
        </Section>
      ) : null}
      <Section label="Decision options">
        <ul className="space-y-1 text-xs text-muted-fg">
          <li>
            <strong className="text-fg">Approve</strong> — release the customer / lift
            the block; record reasons.
          </li>
          <li>
            <strong className="text-fg">Reject (fraud confirmed)</strong> — file SAR,
            keep the block in place, log evidence.
          </li>
          <li>
            <strong className="text-fg">Escalate</strong> — refer to senior analyst /
            compliance officer with current findings.
          </li>
        </ul>
      </Section>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function Section({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <section className="space-y-1.5">
      <h3 className="text-[0.65rem] font-semibold uppercase tracking-wide text-muted-fg">
        {label}
      </h3>
      <div>{children}</div>
    </section>
  )
}

function AlertRow({ a }: { a: AlertSummary }) {
  return (
    <li className="flex items-center justify-between gap-2 rounded border border-border bg-muted/30 p-2 text-xs">
      <Link
        to={`/alerts/${a.id}`}
        className="font-mono text-primary hover:underline"
      >
        {a.alert_number}
      </Link>
      <span className="text-muted-fg">{a.scenario_name}</span>
      <Badge variant={a.priority === 'critical' ? 'critical' : a.priority === 'high' ? 'pending' : 'default'}>
        {a.priority}
      </Badge>
    </li>
  )
}
